import React, { useContext, useEffect, useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { DriverType } from '../../../interfaces/delivery-company/drivers';
import { AuthContext } from '../../../contexts/user-context/user-context';
import {
  Alert,
  CircularProgress,
  LinearProgress,
  TextField,
} from '@mui/material';
import { ErrorsFlattenner } from '../../common/errors-flattenner/errors-flattenner';
import { WhapiChannelScope } from '../../../interfaces/delivery-company/whapi';
import ConfirmAction from '../../common/confirm-action/confirm-action';
import Countdown, { zeroPad } from 'react-countdown';
import { useSnackBar } from '../../../contexts/snackbar';
import PasswordField from '../../common/password-field/password-field';

interface WhatsAppConnectionQRCodeProps {
  scope: WhapiChannelScope;
  require_password?: boolean;
}

export const WhatsAppConnectionQRCode = ({
  scope,
  require_password,
}: WhatsAppConnectionQRCodeProps) => {
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const { user, config } = useContext(AuthContext);
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState<string>('');
  const [reloadQRCountdown, setReloadQRCountdown] = useState<JSX.Element>();

  const fetchImage = async (password?: string) => {
    setIsLoading(true);
    setMessage('');
    setImageBase64(null);
    setResponse(undefined);
    try {
      const response = await axios.get(
        `${API_ENDPOINT}/api/v1/whatsapp/qrcode/?scope=${encodeURIComponent(scope)}&password=${encodeURIComponent(password ?? '')}`,
        config
      );
      setImageBase64(response.data.image);
      setReloadQRCountdown(
        <Countdown
          date={Date.now() + (response.data.expires_after ?? 0) * 1000}
          onComplete={() => fetchImage(password)}
          renderer={({ hours, minutes, seconds, completed }) => (
            <span>
              {zeroPad(minutes)}:{zeroPad(seconds)}
            </span>
          )}
        />
      );
      setResponse(response);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage('Failed to get QR code: ' + e.response?.statusText);
      } else {
        setMessage('Failed to get QR code, please try again later.');
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!require_password) fetchImage();
  }, []);

  return (
    <div style={{ width: '100%', textAlign: 'center' }}>
      {isLoading ? (
        <>
          Please keep this dialog open while we get the connection QR code.{' '}
          <br /> <br />
          <CircularProgress />
        </>
      ) : (
        <>
          {message != '' && (
            <>
              <Alert
                severity={
                  response && response.status >= 200 && response.status < 300
                    ? 'success'
                    : 'error'
                }
              >
                <>
                  {message}

                  {response && response.status >= 300 && (
                    <ErrorsFlattenner rawErrors={response.data} />
                  )}
                </>
              </Alert>
              <br />{' '}
            </>
          )}

          {imageBase64 && (
            <div style={{ width: '100%', margin: 'auto', textAlign: 'center' }}>
              <div>{reloadQRCountdown}</div>
              <div>
                <img src={`${imageBase64}`} alt="QR Code" />
              </div>
            </div>
          )}
        </>
      )}

      {imageBase64 == null && require_password && !isLoading && !response && (
        <ConfirmAction
          message={'Please enter your password'}
          action={function (): void {
            fetchImage(password);
          }}
          isOpen={true}
          handleClose={() => {
            /*Do nothing */
          }}
        >
          {' '}
          You need to enter your password in order to get the QR code
          <br />
          <br />
          <PasswordField
            label="Password "
            placeholder="Enter your account password"
            value={password}
            onPasswordChange={(value) => setPassword(value)}
          />
          <br />
        </ConfirmAction>
      )}
    </div>
  );
};
